import React, { lazy, Suspense } from "react";
import { FaFileInvoice, FaUser, FaCalendarCheck } from "react-icons/fa";
import { MdOutlineSwapHoriz } from "react-icons/md";
import { RiCameraLensLine } from "react-icons/ri";
import { PiCurrencyCircleDollarBold } from "react-icons/pi";
import { FaCheck } from "react-icons/fa6";
import { Helmet } from "react-helmet-async";

const Whywaggexcommonchoice = lazy(
  () => import("./common/Whywaggexcommonchoice"),
);
const LookingforPayroll = lazy(() => import("./common/LookingforPayroll"));
const HomeFaq = lazy(() => import("../../pages/HomeFaq"));
const AppDownloadCard = lazy(() => import("../common/AppDownloadCard"));
const PayrollBanner = lazy(() => import("../common/PayrollBanner"));
const ModernizeHR = lazy(() => import("../common/ModernizeHR"));

const NewDelhi = () => {
  const delhifaq1 = [
    {
      title: "Why do businesses in Delhi choose Waggex Payroll Software?",
      content:
        "Waggex helps businesses automate payroll processing, attendance tracking, leave management, employee records, and payslip generation from one centralized platform. It is designed for Indian businesses that want accurate payroll, reduced manual work, and affordable HR automation.",
    },
    {
      title: "Does Waggex support attendance-based payroll calculations?",
      content:
        "Yes. Waggex automatically calculates salaries using attendance records, leave data, shift schedules, overtime, and payroll rules, helping businesses generate accurate payroll every month.",
    },
    {
      title: "Can employees mark attendance using their mobile phones?",
      content:
        "Yes. Employees can mark attendance through the Waggex Staff App using GPS-based attendance, selfie attendance, geo-fencing, and FaceLens AI face recognition, depending on company requirements.",
    },
    {
      title: "Does Waggex offer AI face recognition attendance?",
      content:
        "Yes. Waggex FaceLens uses AI-powered face recognition technology to verify employee attendance, helping businesses reduce buddy punching and improve attendance accuracy.",
    },
  ];
  const delhifaq2 = [
    {
      title: "Is Waggex suitable for small businesses and startups in Delhi?",
      content:
        "Absolutely. Waggex is designed for startups, SMEs, and growing businesses looking for affordable payroll and HR software without the complexity and cost of enterprise solutions.",
    },
    {
      title: "Does Waggex include leave and shift management?",
      content:
        "Yes. Managers can create shifts, assign schedules, track leave balances, approve requests, and manage workforce availability through a single dashboard.",
    },
    {
      title: "Can employees download payslips online?",
      content:
        "Yes. Employees can securely view and download their payslips through the Waggex Staff App or employee portal anytime.",
    },
    {
      title: "Does Waggex provide payroll reports and compliance records?",
      content:
        "Yes. Waggex provides payroll reports, salary records, attendance summaries, employee data, and payroll-ready reports that help businesses maintain organized HR and payroll records.",
    },
  ];
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",

      name: "Waggex Payroll Software Delhi",

      url: "https://www.waggex.com/best-payroll-software-in-delhi/",

      image: "https://www.waggex.com/waggex-payroll-software-logo.png",

      description:
        "Waggex is payroll and HRMS software for Delhi businesses, automating payroll processing, attendance tracking, leave management, employee records, and workforce management.",

      applicationCategory: "BusinessApplication",

      operatingSystem: "Web, Android, iOS",

      featureList: [
        "Payroll software Delhi",
        "Attendance management software Delhi",
        "Leave and shift management Delhi",
        "Face recognition attendance Delhi",
        "Employee self-service app",
        "GPS attendance tracking",
        "Payroll automation",
        "HRMS software",
      ],

      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
        description: "Free Demo Available",
      },

      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.8",
        ratingCount: "250",
      },

      provider: {
        "@type": "Organization",
        name: "Waggex",
        url: "https://www.waggex.com/",
        logo: "https://www.waggex.com/waggex-payroll-software-logo.png",

        contactPoint: {
          "@type": "ContactPoint",
          telephone: "+91-01724534553",
          email: "info@waggex.com",
          contactType: "customer support",
          availableLanguage: ["English", "Hindi"],
        },
      },
    },

    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "Which is the best payroll software in Delhi?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "The best payroll software depends on your business requirements. Waggex helps Delhi businesses automate payroll, attendance management, leave tracking, and payslip generation from one connected platform.",
          },
        },
        {
          "@type": "Question",
          name: "Does Waggex support attendance-based payroll?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex links employee attendance, approved leave, and working hours directly to payroll, helping businesses calculate salaries accurately.",
          },
        },
        {
          "@type": "Question",
          name: "Does Waggex provide mobile attendance?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Employees can mark attendance through the Waggex Staff App using GPS attendance, selfie attendance, and mobile check-ins.",
          },
        },
        {
          "@type": "Question",
          name: "Does Waggex support face recognition attendance?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex offers AI-powered face recognition attendance to help prevent proxy attendance and improve workforce accountability.",
          },
        },
        {
          "@type": "Question",
          name: "Is Waggex suitable for small businesses in Delhi?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes. Waggex is designed for startups, SMEs, and growing businesses looking for affordable payroll and HR software.",
          },
        },
      ],
    },

    {
      "@context": "https://schema.org",
      "@type": "Organization",

      name: "Waggex",

      url: "https://www.waggex.com/",

      logo: "https://www.waggex.com/waggex-payroll-software-logo.png",

      description:
        "Waggex provides payroll software, attendance management, leave management, HRMS, and workforce management solutions for businesses across India.",

      contactPoint: {
        "@type": "ContactPoint",
        telephone: "+91-01724534553",
        email: "info@waggex.com",
        contactType: "customer support",
        availableLanguage: ["English", "Hindi"],
      },

      sameAs: ["https://www.linkedin.com/company/waggex"],
    },

    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",

      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.waggex.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Best Payroll Software in Delhi",
          item: "https://www.waggex.com/best-payroll-software-in-delhi/",
        },
      ],
    },
  ];

  return (
    <>
      <Helmet>
        <title>Best Payroll Software In Delhi For Businesses & SMEs</title>
        <meta
          name="description"
          content="Discover the best payroll software in Delhi for SMEs and businesses with automated payroll, compliance, tax management, and HR tools"
        />
        <meta
          name="keywords"
          content="payroll software in Delhi,payroll software Delhi,HR software for businesses in Delhi,attendance management software Delhi,HRMS software Delhi NCR,online payroll system Delhi,employee management software Delhi,leave management software Delhi,face recognition attendance Delhi,payroll software for SMEs Delhi"
        />
        <link
          rel="canonical"
          href="https://www.waggex.com/best-payroll-software-in-delhi"
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(schema),
          }}
        />
      </Helmet>
      <section className="bg-[linear-gradient(180deg,#D1DFEA_15.91%,#FFFFFF_145%)] py-10 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <h1 className="text-center text-[#10324F] text-lg sm:text-xl md:text-3xl font-bold uppercase leading-snug">
            Payroll Software in Delhi for SMEs
          </h1>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center mt-8 sm:mt-10">
            {/* Left Content */}
            <div>
              <h2 className="text-[#10324F] text-lg sm:text-xl md:text-2xl font-bold leading-snug">
                Complete Payroll, Attendance & HR Software
              </h2>

              <p className="mt-5 text-[#10324F] text-sm sm:text-base leading-relaxed">
                Managing employees requires more than just payroll. Businesses
                need accurate attendance records, leave tracking, employee data
                management, salary processing, payslip generation, and
                compliance-ready records that stay connected throughout the
                payroll cycle
              </p>

              <p className="mt-5 text-[#10324F] text-sm sm:text-base leading-relaxed">
                Waggex brings payroll, attendance, leave management, employee
                records, selfie attendance, and payroll automation into one
                platform. Built for growing businesses in Delhi, it helps HR
                teams reduce manual work, improve payroll accuracy, and manage
                their workforce from a single dashboard
              </p>
            </div>

            {/* Right Card */}
            <div className="rounded-xl border border-white p-4 sm:p-6 bg-white/30">
              <h2 className="text-[#10324F] font-medium text-base sm:text-lg mb-4 text-center lg:text-left">
                All in One Software - Waggex
              </h2>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="bg-white/40 border border-white rounded-xl p-3 sm:p-4 flex flex-col items-center text-center">
                  <PiCurrencyCircleDollarBold className="text-2xl sm:text-3xl text-[#10324F]" />
                  <span className="mt-2 text-xs sm:text-sm font-medium text-[#10324F]">
                    Payroll
                    <br />
                    Management
                  </span>
                </div>

                <div className="bg-white/40 border border-white rounded-xl p-3 sm:p-4 flex flex-col items-center text-center">
                  <FaCalendarCheck className="text-2xl sm:text-3xl text-[#10324F]" />
                  <span className="mt-2 text-xs sm:text-sm font-medium text-[#10324F]">
                    Attendance
                    <br />
                    Management
                  </span>
                </div>

                <div className="bg-white/40 border border-white rounded-xl p-3 sm:p-4 flex flex-col items-center text-center">
                  <MdOutlineSwapHoriz className="text-2xl sm:text-3xl text-[#10324F]" />
                  <span className="mt-2 text-xs sm:text-sm font-medium text-[#10324F]">
                    Leave & Shift
                    <br />
                    Management
                  </span>
                </div>

                <div className="bg-white/40 border border-white rounded-xl p-3 sm:p-4 flex flex-col items-center text-center">
                  <FaUser className="text-2xl sm:text-3xl text-[#10324F]" />
                  <span className="mt-2 text-xs sm:text-sm font-medium text-[#10324F]">
                    Staff App
                    <br />
                    (Employees)
                  </span>
                </div>

                <div className="bg-white/40 border border-white rounded-xl p-3 sm:p-4 flex flex-col items-center text-center">
                  <RiCameraLensLine className="text-2xl sm:text-3xl text-[#10324F]" />
                  <span className="mt-2 text-xs sm:text-sm font-medium text-[#10324F]">
                    Selfie
                    <br />
                    Attendance
                  </span>
                </div>

                <div className="bg-white/40 border border-white rounded-xl p-3 sm:p-4 flex flex-col items-center text-center">
                  <FaFileInvoice className="text-2xl sm:text-3xl text-[#10324F]" />
                  <span className="mt-2 text-xs sm:text-sm font-medium text-[#10324F]">
                    Payslip
                    <br />
                    Generation
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Everything Growing Delhi Businesses Need In One Payroll Platform
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base leading-relaxed">
              From office teams and retail staff to field employees and
              multi-location operations, Delhi businesses need payroll software
              that goes beyond salary processing. Waggex combines payroll,
              attendance, leave management, employee records, and workforce
              automation into one connected platform
            </p>
          </div>
          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Automated Payroll Processing</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Shift Management</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Attendance-Based Salary Calculation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>PDF Payslip Generation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>AI Selfie Attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee Self-Service App</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>GPS Attendance Tracking</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Real-Time Notifications</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee Leave Management</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Face Recognition Attendance</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-[#F3F5F7] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Why Smart Businesses Are Moving to Automated Payroll
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Growing businesses cannot afford payroll delays, salary errors, or
              disconnected HR processes.<strong>Waggex</strong> helps companies
              streamline payroll operations while improving visibility, control,
              and employee experience
            </p>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Businesses across Delhi use <strong>Waggex</strong> to reduce
              manual work, centralize employee records, and process payroll with
              greater confidence every month
            </p>
          </div>
          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Automated payroll calculation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee salary reports</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Attendance-based payroll processing</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Payroll history and records</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Overtime and shift salary management</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Employee payroll notifications</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Payslip generation in PDF format</span>
              </div>
            </div>
          </div>{" "}
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center max-w-3xl mx-auto leading-loose">
            Instead of managing payroll through multiple spreadsheets and
            disconnected tools, businesses can handle salaries, employee
            records, attendance data, and workforce operations from one
            centralized platform
          </p>
        </div>
      </section>
      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Attendance Tracking Built for Office, Field & Remote Teams
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Accurate attendance tracking helps businesses improve workforce
              accountability, reduce payroll disputes, and gain better
              visibility into employee working hours
            </p>
          </div>
          {/* List */}
          <div className="mt-8 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>AI Selfie Attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Shift-Based Attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>GPS Attendance Tracking</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Mobile Attendance Marking</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Geofencing</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Real-Time Attendance Reports</span>
              </div>
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>FaceLens AI Face Recognition Attendance</span>
              </div>
            </div>
          </div>{" "}
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center">
            Track attendance, leaves, shifts, and working hours from one
            centralized platform while keeping payroll calculations accurate and
            up to date
          </p>
        </div>
      </section>
      <section className="bg-[#F3F5F7] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Simplify Employee Leave & Shift Planning
            </h2>

            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Manage employee leave requests, shift schedules, and workforce
              availability through one streamlined system designed for growing
              teams
            </p>
          </div>

          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Yearly leave allocation</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Leave approval workflow</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Leave balance tracking</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Real-time leave notifications</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Attendance-based leave management</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Shift management</span>
              </div>
            </div>{" "}
          </div>
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center max-w-2xl mx-auto leading-loose">
            Managers gain better visibility into employee availability, while
            staff can submit leave requests, track approvals, and stay updated
            on shift schedules from anywhere
          </p>
        </div>
      </section>
      <section className="bg-[#FFFFFF] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Dedicated Staff App for Employees
            </h2>
            <p className="mt-4 text-[#10324F] text-sm sm:text-base">
              Give employees instant access to attendance, leave requests,
              payslips, and important workplace information through the
              <strong> Waggex Staff App</strong>
            </p>
            <p className="mt-4 text-[#10324F] text-lg">Employee Can:</p>
          </div>
          {/* List */}
          <div className="mt-8 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Mark attendance</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>View attendance history</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Apply for leave </span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Receive notifications</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Download payslips</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Access employee records</span>
              </div>
            </div>
          </div>{" "}
          <p className="mt-8 text-[#10324F] text-sm sm:text-base text-center">
            The <strong> Waggex Staff App</strong> empowers employees with
            self-service access while helping HR teams reduce routine queries
            and administrative workload
          </p>
        </div>
      </section>
      <section className="bg-[#F3F5F7] py-10 px-6">
        <div className="max-w-6xl mx-auto">
          {/* Heading */}
          <div className="text-center">
            <h2 className="text-[#10324F] text-xl md:text-3xl font-bold uppercase">
              Built for Businesses of Every Size and Industry
            </h2>

            <p className="mt-4 text-[#10324F] text-lg">
              Waggex is suitable for:
            </p>
          </div>

          {/* List */}
          <div className="mt-10 flex justify-center">
            <div className="grid grid-cols-2 gap-x-5 md:gap-x-20 gap-y-5">
              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Startups</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Factories</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>SMEs</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Service Companies</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>IT Companies</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Field Workforce Businesses</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Retail Businesses</span>
              </div>

              <div className="flex items-center gap-3 text-[#10324F]">
                <FaCheck className="mt-1 shrink-0 text-xl" />
                <span>Agencies & Consultancies</span>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Suspense fallback={null}>
        <Whywaggexcommonchoice
          heading="Why Waggex Is a Smart Choice for Delhi Businesses?"
          description="Businesses across Delhi use Waggex to automate payroll, track attendance, manage employee records, and simplify daily HR operations. Designed for startups, SMEs, service companies, and field workforce teams, Waggex helps reduce manual work, improve compliance, and keep workforce data organized in one centralized platform"
          paragraph=""
        />
        <LookingforPayroll
          heading="Looking For Payroll Software In Chennai?"
          subHeading="Book a Free Demo and Explore Waggex Features"
          description="Manage payroll, attendance, leaves, shifts, and employee operations using one smart HR and payroll platform"
          features={[
            "AI Attendance Management",
            "Payroll Automation",
            "Employee Self-Service App",
            "GPS & Face Recognition Attendance",
            "Attendance-Based Payroll",
            "Real-Time Notifications",
          ]}
          buttonText="Book Free Demo"
        />
        <HomeFaq
          faq1={delhifaq1}
          faq2={delhifaq2}
          subtitle="Everything you need to know about Waggex payroll software in India"
        />
        {/*  <AppDownloadCard /> */}
        <PayrollBanner
          heading="Ready to Automate Payroll, Attendance & HR?"
          description="See how Waggex helps businesses reduce manual work, improve payroll accuracy, and manage employees more efficiently with one easy-to-use platform"
          buttonText="View HRMS Software"
          buttonLink="https://waggex.com/"
        />
        <AppDownloadCard />
        <ModernizeHR />
      </Suspense>
    </>
  );
};

export default NewDelhi;
